<template>
    <div class="card" style="box-shadow: none">
        <div class="card-header">
            <h4 class="card-title">
                {{ $t('users.shops_msg__edit_a_shop') }}
            </h4>
        </div>
        <form class="card-body me-3 ms-3" @submit.prevent="handleSubmit">
            <h4 class="mb-3">
                {{ $t('users.shops_msg__shop_info') }}
            </h4>
            <div
                v-for="(inputGroup, index) in formConfig"
                :key="'shop-' + index"
            >
                <!-- 單一物件的情況 -->
                <div v-if="!Array.isArray(inputGroup)" class="form-group-row">
                    <!-- dual-select 類型使用 DualSelectInput -->
                    <DualSelectInput
                        v-if="inputGroup.type === 'dual-select'"
                        ref="inputRefs"
                        v-bind="inputGroup"
                        v-model="formData[inputGroup.field]"
                        @valid="handleValidation"
                        @options-loaded="updateConfigOptions"
                    />
                    <!-- 其他類型使用 UserFormGroupInput -->
                    <UserFormGroupInput
                        v-else
                        ref="inputRefs"
                        v-bind="inputGroup"
                        v-model="formData[inputGroup.field]"
                        @valid="handleValidation"
                        @options-loaded="updateConfigOptions"
                    />
                </div>
                <!-- 多個物件（數組）的情況 -->
                <div v-else class="form-group-row d-flex">
                    <div
                        v-for="(inputConfig, subIndex) in inputGroup"
                        :key="'shop-sub-' + subIndex"
                        class="flex-fill"
                    >
                        <!-- dual-select 類型使用 DualSelectInput -->
                        <DualSelectInput
                            v-if="inputConfig.type === 'dual-select'"
                            ref="inputRefs"
                            v-bind="inputConfig"
                            v-model="formData[inputConfig.field]"
                            @valid="handleValidation"
                            @options-loaded="updateConfigOptions"
                        />
                        <!-- 其他類型使用 UserFormGroupInput -->
                        <UserFormGroupInput
                            v-else
                            ref="inputRefs"
                            v-bind="inputConfig"
                            v-model="formData[inputConfig.field]"
                            @valid="handleValidation"
                            @options-loaded="updateConfigOptions"
                        />
                    </div>
                </div>
            </div>
            <div class="d-grid gap-2 d-md-flex justify-content-md-start">
                <button
                    type="submit"
                    class="btn btn-primary"
                    :disabled="submitDisabled"
                >
                    {{ $t('users.users_msg__user_form_submit') }}
                </button>
                <button
                    type="button"
                    class="btn btn-secondary"
                    @click="handleReset"
                >
                    {{ $t('users.users_msg__create_account_reset') }}
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import { ref, onMounted, inject, watch } from 'vue';
import { UserFormGroupInput, DualSelectInput } from '@/components';
import { useI18n } from 'vue-i18n';
import { fetchEditShopConfig, editShop, getShop } from '@/api/users';
import {
    transformFormPayload,
    transformValueForForm,
    transformValueByPayloadType,
} from '@/utils/formPayload';

export default {
    name: 'ShopEdit',
    components: {
        UserFormGroupInput,
        DualSelectInput,
    },
    props: {
        shop: {
            type: Array,
            required: true,
        },
    },
    setup(props, { emit }) {
        const formConfig = ref([]);
        const formData = ref({});
        const submitDisabled = ref(false);
        const { t } = useI18n();
        const Swal = inject('$swal');

        const shop = ref({});
        const inputRefs = ref([]);

        // 輔助函數：找到特定類型的字段
        const findFieldByType = (configs, fieldName, fieldType = null) => {
            for (const config of configs) {
                if (Array.isArray(config)) {
                    const found = findFieldByType(config, fieldName, fieldType);
                    if (found) return found;
                } else {
                    if (config.field === fieldName) {
                        if (!fieldType || config.type === fieldType) {
                            return config;
                        }
                    }
                }
            }
            return null;
        };

        // 輔助函數：找到 stations 字段
        const findStationField = configs => {
            return findFieldByType(configs, 'stations');
        };

        const initialize = async () => {
            await loadShopDetail();
            await loadFormConfig(); // 這裡會在賦值前就設置好正確的 options_url
            initializeFormData();
        };

        onMounted(async () => {
            await initialize();
        });

        const loadFormConfig = async () => {
            try {
                const config = await fetchEditShopConfig();

                // 在賦值給 formConfig.value 之前，先設置正確的 club-based options_url
                // 這樣組件渲染時就會使用正確的 URL
                if (shop.value.club) {
                    const stationField = findFieldByType(config, 'stations');
                    if (stationField) {
                        // 設置根據 club 過濾的 stations URL
                        stationField.options_url = `/users/stations?search_fields=club__id&search=${shop.value.club}&search_mode=exact`;

                        // 設置 stations 的初始值
                        setupStationFieldValue(stationField);
                    }
                }

                formConfig.value = config;
            } catch (error) {
                console.error('Error fetching form configuration:', error);
            }
        };

        // 設置 stations 欄位的初始值
        const setupStationFieldValue = stationField => {
            // 根據類型設置初始值
            if (
                stationField.type === 'dual-select' ||
                stationField.type === 'multi-select'
            ) {
                // dual-select / multi-select: 需要 ID 陣列
                if (
                    shop.value.stations_details &&
                    Array.isArray(shop.value.stations_details)
                ) {
                    formData.value.stations = shop.value.stations_details.map(
                        st => st.id
                    );
                } else if (
                    shop.value.stations &&
                    Array.isArray(shop.value.stations)
                ) {
                    formData.value.stations = shop.value.stations;
                } else {
                    formData.value.stations = [];
                }
            } else if (stationField.type === 'select') {
                // select: 從 stations_details 取得 ID 陣列，再用 transformValueForForm 轉換為單一值
                let rawValue = [];
                if (
                    shop.value.stations_details &&
                    Array.isArray(shop.value.stations_details)
                ) {
                    rawValue = shop.value.stations_details.map(st => st.id);
                } else if (
                    shop.value.stations &&
                    Array.isArray(shop.value.stations)
                ) {
                    rawValue = shop.value.stations;
                }
                const transformedValue = transformValueForForm(
                    rawValue,
                    stationField
                );
                formData.value.stations = transformedValue;
                // 同時更新 config.value，讓 UserFormGroupInput 可以正確顯示
                stationField.value = transformedValue;
            }
        };

        const loadShopDetail = async () => {
            try {
                shop.value = await getShop(props.shop[0]);
            } catch (error) {
                console.error('Error fetching shop detail:', error);
                if (error.response && error.response.status === 404) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: t('users.shops_msg__shop_not_found'),
                    }).then(() => {
                        // Close the modal
                        emit('resetModal');
                    });
                }
            }
        };

        const initializeFormData = () => {
            formConfig.value.forEach(config => {
                if (Array.isArray(config)) {
                    config.forEach(subConfig => {
                        initializeSingleField(subConfig);
                    });
                } else {
                    initializeSingleField(config);
                }
            });
        };

        // 初始化單一欄位
        const initializeSingleField = config => {
            const field = config.field;

            // stations 字段已在 loadFormConfig 中處理，跳過
            if (field === 'stations' && formData.value.stations !== undefined) {
                return;
            }

            if (
                config.type === 'multi-select' ||
                config.type === 'dual-select'
            ) {
                // 對於 multi-select 和 dual-select，確保預設值是 ID 陣列
                if (Array.isArray(shop.value[field])) {
                    formData.value[field] = shop.value[field];
                } else {
                    formData.value[field] = [];
                }
            } else if (config.type === 'select') {
                // select 類型：處理可能返回陣列的情況
                if (Array.isArray(shop.value[field])) {
                    formData.value[field] = shop.value[field][0] || '';
                    config.value = shop.value[field][0] || '';
                } else {
                    formData.value[field] = shop.value[field] || '';
                    config.value = shop.value[field] || '';
                }
            } else if (Array.isArray(shop.value[field])) {
                formData.value[field] = shop.value[field].join(', ') || '';
                config.value = shop.value[field].join(', ') || '';
            } else {
                formData.value[field] = shop.value[field] || '';
                config.value = shop.value[field] || '';
            }
        };

        // 監控 user prop 的變化，更新表單數據
        watch(() => props.shop, initializeFormData);

        const handleValidation = isValid => {
            submitDisabled.value = !isValid;
        };

        const handleSubmit = async () => {
            inputRefs.value.forEach(ref => {
                if (ref && ref.checkValue) {
                    ref.checkValue();
                }
            });

            // 檢查必填的 dual-select 欄位
            const stationField = findStationField(formConfig.value);
            if (
                stationField &&
                stationField.type === 'dual-select' &&
                stationField.required === 'true'
            ) {
                if (
                    !formData.value.stations ||
                    formData.value.stations.length === 0
                ) {
                    Swal.fire({
                        icon: 'warning',
                        title: t(
                            'users.shops_msg__select_at_least_one_station'
                        ),
                        text: t('users.shops_msg__please_select_station'),
                    });
                    return;
                }
            }

            if (submitDisabled.value) {
                return;
            }
            Swal.fire({
                title: t('users.shops_msg__editing_shop'),
                text: t('users.shops_msg__please_wait'),
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                },
            });

            // 使用 transformFormPayload 處理 payload 格式轉換（編輯模式：跳過未變更的欄位）
            const shopData = transformFormPayload(
                formData.value,
                formConfig.value,
                {
                    originalData: shop.value,
                    skipUnchanged: true,
                }
            );

            // 確保 stations 被包含在提交資料中（如果有變更）
            // 需要根據 payload_type 轉換值
            if (stationField && formData.value.stations !== undefined) {
                // 取得原始的 stations ID 陣列用於比較
                let originalStations = [];
                if (
                    shop.value.stations_details &&
                    Array.isArray(shop.value.stations_details)
                ) {
                    originalStations = shop.value.stations_details.map(
                        st => st.id
                    );
                } else if (
                    shop.value.stations &&
                    Array.isArray(shop.value.stations)
                ) {
                    originalStations = shop.value.stations;
                }

                // 使用 transformValueByPayloadType 轉換當前值
                const transformedStations = transformValueByPayloadType(
                    formData.value.stations,
                    stationField.payload_type
                );

                // 比較是否有變更
                if (
                    JSON.stringify(transformedStations) !==
                    JSON.stringify(originalStations)
                ) {
                    shopData.stations = transformedStations;
                }
            }

            const shopId = props.shop[0];
            Swal.close();
            if (Object.keys(shopData).length === 0) {
                Swal.fire({
                    icon: 'info',
                    title: t('users.users_msg__no_changes_have_been_made'),
                });
                return;
            }
            try {
                await editShop(shopId, shopData);
                await Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: t('users.shops_msg__edit_a_shop_successful', {
                        name: shopData.name,
                    }),
                });
                emit('submitSuccess');
                emit('resetModal'); // 關閉 modal
            } catch (error) {
                await Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: t(`users.${error.response.data.detail}`),
                });
            }
        };

        const handleReset = async () => {
            await initialize();
        };

        const updateConfigOptions = ({ field, options }) => {
            // Find and update the config for the specified field
            const findAndUpdateConfig = configs => {
                for (const config of configs) {
                    if (Array.isArray(config)) {
                        findAndUpdateConfig(config);
                    } else if (config && config.field === field) {
                        // Update the options in the config
                        config.options = options;
                        return true;
                    }
                }
                return false;
            };
            findAndUpdateConfig(formConfig.value);
        };

        return {
            formConfig,
            formData,
            submitDisabled,
            handleSubmit,
            handleValidation,
            handleReset,
            inputRefs,
            updateConfigOptions,
        };
    },
};
</script>

<style scoped>
.form-group-row {
    margin-bottom: 1rem;
    text-align: left;
}

.d-flex {
    display: flex;
    gap: 1rem;
}

.flex-fill {
    flex: 1;
}
</style>
